import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { PatronService } from '@app/services/patron.service';
import { commonComponents } from '@app/shared/common components/common components';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import * as feather from 'feather-icons';

@Component({
  standalone: false,
  selector: 'app-patrons-list-page',
  templateUrl: './patrons-list-page.component.html',
  styleUrls: ['./patrons-list-page.component.css'],
})
export class PatronsListPage implements OnInit {
  candidates: any[] = [];
  filteredCandidates: any[] = [];
  loading = false;
  isLoading = false;
  vid: any;
  searchTerm = '';
  confirmDeleteId: number | null = null;
  errorMS: string;
  currentPage = 1;
  itemsPerPage = 25;
  sortField = '';
  sortDirection = 1;
  sortIcon: Record<string, string> = {
    id: '',
    candidate_id: '',
    name: '',
    email: '',
    job_ref_id: '',
    created_by: '',
    created_on: '',
  };
  errorMessage: string | null = null;
  error: any;
  showFormModal = false;
  formData: any;
  errorDisplay: string;
  isChecked = false;
  enable = false;
  resubmit = false;
  headers = commonComponents.PatronList.headers;
  tableBodyData: any = [];
  btnDissble = false;
  tosterMessage = {
    message: '',
    type: '',
  };
  noDataFound = false;
  isValid: any[];

  constructor(private patronService: PatronService, private router: Router, private route: ActivatedRoute) {}

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.searchTerm = params['searchTerm'] || '';
      this.currentPage = +params['currentPage'] || 1;
      this.sortField = params['sortField'] || '';
      this.sortDirection = +params['sortDirection'] || 1;
      this.enable = params['enable'] === 'true';
      this.loadCandidates();
    });
    if (this.enable === true) {
      this.isChecked = true;
    } else {
      this.isChecked = false;
    }
  }

  ngAfterViewInit() {
    feather.replace();
  }

  loadCandidates(): void {
    this.isLoading = true;
    this.patronService
      .loadCandidates()
      .pipe(
        catchError(() => {
          this.setErrorMessage('Failed to load candidates');
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe((res) => {
        const candidateslist = res;
        const allCandidates = candidateslist.data;
        this.candidates = allCandidates.sort((a, b) => a.id - b.id);
        this.filteredCandidates = this.candidates;
        this.filterCandidates();
        this.tableBody(this.candidates);
      });
  }

  tableBody(data: any) {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const paginatedData = data.slice(startIndex, endIndex);
    this.tableBodyData = paginatedData.map((candidate) => {
      candidate.apis.forEach((api) => {
        const created_at = new Date(`${api.created_on}z`).toLocaleString();
        const result = {
          assessment_decision: api.assessment_decision,
          created_at,
        };
        if (api.api_name === 'ITRAK Exclusion List') {
          candidate.itrak_status = result;
        } else if (api.api_name === 'Gaming Exclusion List') {
          candidate.gaming_status = result;
        } else if (api.api_name === 'Age Verification') {
          candidate.age_verification_status = result;
        } else if (api.api_name === 'Sanctions') {
          candidate.sanctions_status = result;
        }
      });

      return {
        id: candidate.id,
        name: candidate.name,
        email: candidate.email,
        gaming_status: candidate.gaming_status,
        itrak_status: candidate.itrak_status,
        age_verification_status: candidate.age_verification_status,
        sanctions_status: candidate.sanctions_status,
        candidate_id: candidate.candidate_id,
        screened_by: `${candidate.user_id} - ${candidate.user_email}`,
      };
    });
  }

  filterCandidates(): void {
    this.searchTerm = this.searchTerm.trim().toLowerCase();
    if (this.searchTerm) {
      const filteredCandidates = this.candidates.filter((candidate) => {
        const matchId = candidate.id.toString().toLowerCase().includes(this.searchTerm);
        const matchName = candidate.name.toLowerCase().includes(this.searchTerm);
        const matchEmail = candidate.email.toLowerCase().includes(this.searchTerm);
        return matchId || matchName || matchEmail;
      });
      if (filteredCandidates.length > 0) {
        this.currentPage = 1;
        this.tableBody(filteredCandidates);
        this.noDataFound = false;
      } else {
        this.clearTable();
        this.noDataFound = true;
      }
    } else {
      console.log('No search term, displaying all candidates.');
      this.tableBody(this.candidates);
      this.noDataFound = false;
    }

    this.sortCandidates();
  }
  clearTable() {
    this.tableBodyData = [];
    this.isLoading = false;
  }

  get totalPages(): number {
    return Math.ceil(this.filteredCandidates.length / this.itemsPerPage);
  }

  get paginatedCandidates() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const htmldata = this.filteredCandidates.map((i) => ({
      ...i,
      idv_status: i.idv_status ? i.idv_status.toUpperCase() : 'NO STATUS',
      bg_status: i.bg_status ? i.bg_status.toUpperCase() : 'NO STATUS',
    }));
    return htmldata.slice(startIndex, startIndex + this.itemsPerPage);
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateQueryParams();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateQueryParams();
    }
  }

  goToFirstPage(): void {
    this.currentPage = 1;
    this.updateQueryParams();
  }

  goToLastPage(): void {
    this.currentPage = this.totalPages;
    this.updateQueryParams();
  }

  changeSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = -this.sortDirection;
    } else {
      this.sortField = field;
      this.sortDirection = 1;
    }
    this.sortCandidates();
    this.updateQueryParams();
  }

  sortCandidates(): void {
    this.filteredCandidates.sort((a, b) => {
      const aValue = this.sortField === 'name' ? a.name.toLowerCase() : a[this.sortField];
      const bValue = this.sortField === 'name' ? b.name.toLowerCase() : b[this.sortField];

      const isANullOrEmpty = aValue === null || aValue === '' || aValue === undefined;
      const isBNullOrEmpty = bValue === null || bValue === '' || bValue === undefined;

      if (isANullOrEmpty && !isBNullOrEmpty) {
        return 1;
      } else if (!isANullOrEmpty && isBNullOrEmpty) {
        return -1;
      }

      if (aValue < bValue) {
        return -1 * this.sortDirection;
      } else if (aValue > bValue) {
        return 1 * this.sortDirection;
      }
      return 0;
    });

    Object.keys(this.sortIcon).forEach((key) => (this.sortIcon[key] = ''));
    this.sortIcon[this.sortField] = this.sortDirection === 1 ? '↑' : '↓';
  }

  setErrorMessage(message: string) {
    this.tosterMessage = {
      message: message,
      type: 'error',
    };
  }

  updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        currentPage: this.currentPage !== 1 ? this.currentPage : null,
        sortField: this.sortField || null,
        sortDirection: this.sortDirection !== 1 ? this.sortDirection : null,
        enable: this.enable ? 'true' : 'false',
      },
      queryParamsHandling: 'merge',
    });
  }

  trackByFn(index: number, candidate: any): number {
    return candidate.id;
  }

  assessmentOption(value: any) {
    this.formData = {
      ...this.formData,
      assessment_source: value,
    };
  }

  reupdate() {
    this.resubmit = !this.resubmit;
  }
}
